import { 
    Edit, 
    SimpleForm, 
    TextInput, 
    NumberInput,
    SelectInput,
    BooleanInput,
    DateField,
    required,
    TopToolbar,
    ListButton,
    ShowButton,
    useRecordContext
} from 'react-admin';
import { 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    Box, 
    Avatar,
    Divider,
    Chip
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TipoCartellinoManager from './TipoCartellinoManager';

const EditActions = () => (
    <TopToolbar>
        <ShowButton />
        <ListButton to="/leghe" />
    </TopToolbar>
);

// Componente wrapper per TipoCartellinoManager che accede al record
const TipoCartellinoSection = () => {
    const record = useRecordContext();
    
    if (!record) return null;
    
    return (
        <TipoCartellinoManager 
            legaId={record.id} 
            sportId={record.sportId} 
        />
    );
};

export const Tbl_legaEdit = props => (
    <Edit {...props} actions={<EditActions />} title="Modifica Lega" redirect="/leghe">
        <SimpleForm>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                                    <EditIcon sx={{ fontSize: 32, color: 'white' }} />
                                </Avatar>
                            </Grid>
                            <Grid item xs>
                                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                                    Modifica Lega
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Aggiorna le informazioni della lega
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Chip 
                                    label={`ID: #${props.id || 'N/A'}`} 
                                    sx={{ 
                                        bgcolor: 'rgba(255,255,255,0.2)', 
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }} 
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>

            {/* Informazioni di Sistema */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon color="primary" />
                        Informazioni di Sistema
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <NumberInput 
                                source="id" 
                                label="ID Lega"
                                disabled 
                                fullWidth
                                helperText="Identificativo univoco (non modificabile)"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Data Creazione
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <DateField source="createdAt" showTime locales="it-IT" />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Ultimo Aggiornamento
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <DateField source="updatedAt" showTime locales="it-IT" />
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Informazioni Principali */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmojiEventsIcon color="primary" />
                        Informazioni Principali
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextInput 
                                source="nome" 
                                label="Nome Lega"
                                validate={required()}
                                fullWidth
                                helperText="Nome identificativo della lega"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectInput 
                                source="sportId" 
                                label="Sport"
                                validate={required()}
                                choices={[
                                    { id: 1, name: 'Calcio' },
                                    { id: 2, name: 'Basket' },
                                    { id: 3, name: 'Pallavolo' },
                                    { id: 4, name: 'Tennis' },
                                    { id: 5, name: 'Padel' }
                                ]}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextInput 
                                source="note" 
                                label="Note"
                                multiline
                                rows={4}
                                fullWidth
                                helperText="Note aggiuntive sulla lega"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <NumberInput 
                                source="ordinamento" 
                                label="Ordinamento"
                                fullWidth
                                helperText="Posizione nell'elenco (0 = primo)"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Configurazioni */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Configurazioni
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <BooleanInput 
                                source="flAttivo" 
                                label="Lega Attiva"
                                helperText="La lega è attualmente attiva"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <BooleanInput 
                                source="flVisibile" 
                                label="Lega Visibile"
                                helperText="La lega è visibile pubblicamente"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <BooleanInput 
                                source="flAnnullato" 
                                label="Lega Annullata"
                                helperText="Segna la lega come annullata"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            
            {/* Sezione Tipi Cartellino */}
            <TipoCartellinoSection />
        </SimpleForm>
    </Edit>
);
            